"use client";

import { usePathname } from "next/navigation";
import { useEffect, type ReactNode } from "react";
import { buildPostHogConfig } from "@/core/observability/posthog";
import { useTenantContext } from "@/components/tenant/TenantContextProvider";

declare global {
  interface Window {
    posthog?: {
      init: (
        key: string,
        options: {
          api_host: string;
          capture_pageview: boolean;
          disable_session_recording: boolean;
          before_send?: (event: unknown) => unknown;
          bootstrap?: { distinctID: string; isIdentifiedID: boolean };
        },
      ) => void;
      opt_out_capturing?: () => void;
    };
  }
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const privateLink = /^\/(purchase|apply|email-preferences|accept-invite)(\/|$)/.test(pathname);
  const tenantState = useTenantContext();
  const tenant = tenantState.status === "ready" ? tenantState.tenant : null;
  const config = buildPostHogConfig({
    tenantId: tenant?.tenantId ?? "none",
    tenantAnalyticsOptIn: tenant?.analyticsOptIn ?? false,
    key: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  });

  useEffect(() => {
    if (privateLink || !config.enabled) {
      window.posthog?.opt_out_capturing?.();
      return;
    }

    const init = () =>
      window.posthog?.init(config.key, {
        api_host: config.host,
        capture_pageview: true,
        disable_session_recording: !config.sessionReplay,
        before_send: (event: unknown) => /^\/(purchase|apply|email-preferences|accept-invite)(\/|$)/.test(window.location.pathname) ? null : event,
        bootstrap: { distinctID: config.tenantId, isIdentifiedID: false },
      });

    if (window.posthog) {
      init();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = `${config.host.replace(/\/$/, "")}/static/array.js`;
    script.onload = init;
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [privateLink, config.enabled, config.host, config.key, config.sessionReplay, config.tenantId]);

  return <>{children}</>;
}
